import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { posthogService } from '@/services/posthog-service';
import { UPGRADE_PROMPT_SHOWN, UPGRADE_INITIATED } from '@/services/observability/events';
import type { UpsellGateKey } from '@/constants/upsell-copy';

export interface UpsellSheetProps {
  featureName: string;
  description: string;
  featureGate: UpsellGateKey;
  onDismiss: () => void;
}

/**
 * Reusable bottom-sheet upsell component.
 *
 * Fires UPGRADE_PROMPT_SHOWN on mount, UPGRADE_INITIATED on CTA click.
 * Dismisses on backdrop tap, swipe-down gesture, or Escape key.
 */
export function UpsellSheet({ featureName, description, featureGate, onDismiss }: UpsellSheetProps) {
  const navigate = useNavigate();
  const sheetRef = useRef<HTMLDivElement>(null);

  // Track impression once on mount
  useEffect(() => {
    posthogService.track(UPGRADE_PROMPT_SHOWN, { feature_gate: featureGate });
  }, [featureGate]);

  // Dismiss on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onDismiss();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  // Swipe-down gesture to dismiss
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    let startY = 0;

    function handleTouchStart(e: TouchEvent) {
      startY = e.touches[0]?.clientY ?? 0;
    }

    function handleTouchEnd(e: TouchEvent) {
      const endY = e.changedTouches[0]?.clientY ?? 0;
      if (endY - startY > 60) {
        onDismiss();
      }
    }

    sheet.addEventListener('touchstart', handleTouchStart, { passive: true });
    sheet.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      sheet.removeEventListener('touchstart', handleTouchStart);
      sheet.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onDismiss]);

  function handleUpgradeClick() {
    posthogService.track(UPGRADE_INITIATED, { feature_gate: featureGate });
    void navigate('/subscription');
    onDismiss();
  }

  function handleLearnMore() {
    void navigate('/subscription');
    onDismiss();
  }

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      data-testid={`upsell-sheet-${featureGate}`}
      onClick={(e) => {
        // Dismiss when tapping the backdrop (not the sheet itself)
        if (e.target === e.currentTarget) {
          onDismiss();
        }
      }}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="presentation"
    >
      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`upsell-title-${featureGate}`}
        className="bg-bark-800 border-t-[0.5px] border-bark-600 rounded-t-xl px-6 pt-5 pb-10 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center mb-1">
          <div
            className="w-10 h-1 rounded-full bg-[var(--color-border)]"
            aria-hidden="true"
          />
        </div>

        {/* Pro badge */}
        <div>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-body-xs font-semibold bg-[var(--color-accent-subtle)] text-[var(--color-accent)] border border-[var(--color-accent-pressed)]/40">
            PRO
          </span>
        </div>

        {/* Feature name */}
        <h2
          id={`upsell-title-${featureGate}`}
          className="font-display text-display-sm text-[var(--color-text-display)]"
          data-testid="upsell-feature-name"
        >
          {featureName}
        </h2>

        {/* Description */}
        <p
          className="text-body-sm text-[var(--color-text-secondary)]"
          data-testid="upsell-description"
        >
          {description}
        </p>

        {/* Primary CTA */}
        <button
          type="button"
          onClick={handleUpgradeClick}
          className="w-full py-3 px-4 rounded-lg bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white text-body-md font-semibold transition-colors"
          data-testid="upsell-upgrade-cta"
        >
          Upgrade to Pro
        </button>

        {/* Secondary: Learn more */}
        <button
          type="button"
          onClick={handleLearnMore}
          className="text-body-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] text-center transition-colors"
          data-testid="upsell-learn-more"
        >
          Learn more
        </button>
      </div>
    </div>
  );
}
